import {
  deleteAPI,
  getAPI,
  patchAPI,
  postAPI,
} from '../utils/api';
import endpoints from '../utils/endpoints';

const toApiStatus = (status) => {
  const value = String(status || 'OPEN').toUpperCase().replace(/\s+/g, '_');
  if (value === 'CLOSE') return 'CLOSED';
  if (value === 'HOLD' || value === 'ON_HOLD') return 'IN_PROGRESS';
  if (['RESOLVED', 'OPEN', 'CLOSED', 'IN_PROGRESS'].includes(value)) {
    return value;
  }
  return 'OPEN';
};

const toUiStatus = (status) => {
  const value = String(status || '').toUpperCase().replace(/\s+/g, '_');
  if (value === 'CLOSE') return 'CLOSED';
  if (value === 'HOLD' || value === 'ON_HOLD') return 'IN_PROGRESS';
  if (['RESOLVED', 'OPEN', 'CLOSED', 'IN_PROGRESS'].includes(value)) {
    return value;
  }
  return value || 'OPEN';
};

const toApiPriority = (priority) => {
  const value = String(priority || 'low').toLowerCase();
  if (value === 'high') return 'HIGH';
  if (value === 'moderate' || value === 'medium') return 'MODERATE';
  return 'LOW';
};

const toUiPriority = (priority) => {
  const value = String(priority || '').toUpperCase();
  if (value === 'HIGH') return 'high';
  if (value === 'MODERATE' || value === 'MEDIUM') return 'moderate';
  if (value === 'LOW') return 'low';
  return String(priority || '').toLowerCase();
};

export const allottedTimeToHours = (number, unit) => {
  const value = Number(number);
  if (!Number.isFinite(value) || value <= 0) return 0.01;

  if (unit === 'day') return value * 24;
  if (unit === 'min') return value / 60;
  return value;
};

const resolveUserId = (value) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value[0];
  if (typeof value === 'object') return value.id || value._id;
  return undefined;
};

const resolveAssignedTo = (ticket) => {
  if (Array.isArray(ticket?.assignedTo) && ticket.assignedTo.length) {
    return ticket.assignedTo;
  }

  const assignedUser =
    ticket?.assignedToUser ||
    ticket?.assignedToUserDetails ||
    ticket?.assignee;

  if (assignedUser) {
    return Array.isArray(assignedUser) ? assignedUser : [assignedUser];
  }

  if (ticket?.assignedToUserId) {
    return [
      {
        id: ticket.assignedToUserId,
        username:
          ticket.assignedToUsername ||
          ticket.assignedToUserName ||
          ticket.assigneeName,
        phone: ticket.assignedToPhone || ticket.assignedToUserPhone,
      },
    ];
  }

  return [];
};

const resolveRaisedFor = (ticket) => {
  if (ticket?.raisedFor) return ticket.raisedFor;

  const raisedUser = ticket?.raisedForUser || ticket?.raisedForUserDetails;
  if (raisedUser) return raisedUser;

  if (ticket?.raisedForUserId) {
    return {
      id: ticket.raisedForUserId,
      username:
        ticket.raisedForUsername ||
        ticket.raisedForUserName ||
        ticket.raisedForName,
      phone: ticket.raisedForPhone || ticket.raisedForUserPhone,
    };
  }

  return null;
};

const resolveCreatedBy = (ticket) => {
  if (ticket?.createdBy) return ticket.createdBy;
  if (ticket?.createdByUser) return ticket.createdByUser;
  if (ticket?.raisedByUser) return ticket.raisedByUser;

  if (ticket?.createdByUserId) {
    return {
      id: ticket.createdByUserId,
      username:
        ticket.createdByUsername ||
        ticket.createdByUserName ||
        ticket.createdByName,
      phone: ticket.createdByPhone || ticket.createdByUserPhone,
    };
  }

  return null;
};

export const normalizeTicket = (ticket) => {
  if (!ticket) return null;

  const allottedTimeHours =
    ticket.allottedTimeHours ?? ticket.allotedTimeHours ?? null;

  return {
    ...ticket,
    id: ticket.id || ticket._id,
    _id: ticket._id || ticket.id,
    ticketName: ticket.ticketName,
    ticketDescription: ticket.ticketDescription,
    status: toUiStatus(ticket.status),
    priority: toUiPriority(ticket.priority),
    comment: ticket.comment ?? '',
    assignedTo: resolveAssignedTo(ticket),
    raisedFor: resolveRaisedFor(ticket),
    createdBy: resolveCreatedBy(ticket),
    allotedTime:
      ticket.allotedTime ||
      (allottedTimeHours != null
        ? { number: allottedTimeHours, unit: 'hr' }
        : null),
    allottedTimeHours,
  };
};

export const normalizeTicketsList = (response) => {
  const body = response?.data ?? response;
  const payload = body?.data ?? body;

  const list =
    (Array.isArray(payload) && payload) ||
    payload?.content ||
    payload?.tickets ||
    payload?.data?.content ||
    payload?.data?.tickets ||
    body?.content ||
    [];

  return (Array.isArray(list) ? list : []).map(normalizeTicket).filter(Boolean);
};

export const buildCreateTicketPayload = (formData) => ({
  ticketName: formData.ticketName,
  ticketDescription: formData.ticketDescription,
  allottedTimeHours: allottedTimeToHours(formData.number, formData.unit),
  raisedForUserId: resolveUserId(formData.raisedFor),
  assignedToUserId: resolveUserId(formData.assignedTo),
  status: toApiStatus(formData.status),
  priority: toApiPriority(formData.priority),
  comment: formData.comment || '',
});

export const buildUpdateTicketPayload = (formData) => ({
  ticketName: formData.ticketName,
  ticketDescription: formData.ticketDescription,
  allottedTimeHours: allottedTimeToHours(formData.number, formData.unit),
  raisedForUserId: resolveUserId(formData.raisedFor),
  assignedToUserId: resolveUserId(formData.assignedTo),
  status: toApiStatus(formData.status),
  priority: toApiPriority(formData.priority),
  comment: formData.comment || '',
});

export const getAllTicket = (payload, _userId, callBack, errorCallback) => {
  return (dispatch) => {
    const queryParams = {
      page: payload?.page != null ? Math.max(Number(payload.page) - 1, 0) : 0,
      size: payload?.size ?? payload?.perPage ?? 100,
      search: payload?.search || undefined,
      status: payload?.status ? toApiStatus(payload.status) : undefined,
      priority: payload?.priority
        ? toApiPriority(payload.priority)
        : undefined,
      raisedForUserId: payload?.raisedForUserId || undefined,
      assignedToUserId: payload?.assignedToUserId || undefined,
    };

    getAPI(
      endpoints.TICKETS,
      queryParams,
      (response) => {
        callBack({
          ...response,
          data: {
            ...response.data,
            data: normalizeTicketsList(response),
          },
        });
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem('token')
    );
  };
};

export const getTicketById = (ticketId, callBack, errorCallback) => {
  return (dispatch) => {
    getAPI(
      endpoints.TICKET_BY_ID.replace(':id', ticketId),
      {},
      (response) => {
        const ticket =
          normalizeTicket(response?.data?.data) ||
          normalizeTicket(response?.data) ||
          normalizeTicket(response);
        callBack({ ...response, data: { ...response.data, data: ticket } });
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem('token')
    );
  };
};

export const postTheTicket = (payload, _userId, callBack, errorCallback) => {
  return (dispatch) => {
    const body = buildCreateTicketPayload(payload);

    postAPI(
      endpoints.TICKETS,
      body,
      (response) => {
        callBack(response);
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem('token')
    );
  };
};

export const UpdateTheTicket = (
  payload,
  _userId,
  ticketId,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    patchAPI(
      endpoints.TICKET_BY_ID.replace(':id', ticketId),
      buildUpdateTicketPayload(payload),
      (response) => {
        callBack(response);
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem('token')
    );
  };
};

export const deleteTicket = (
  _userId,
  ticketId,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    deleteAPI(
      endpoints.TICKET_BY_ID.replace(':id', ticketId),
      {},
      (response) => {
        callBack(response);
      },
      (error) => {
        errorCallback(error);
      },
      localStorage.getItem('token')
    );
  };
};

export const getAllRewards = (
  payload,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    const queryParams = {
      page: payload?.page ?? 0,
      size: payload?.size ?? payload?.perPage ?? 20,
      search: payload?.search || undefined,
      giverUserId: payload?.giverUserId || undefined,
      receiverUserId: payload?.receiverUserId || undefined,
      createdFrom: payload?.createdFrom || undefined,
      createdTo: payload?.createdTo || undefined,
    };

    getAPI(
      endpoints.GET_REWARDS_HISTORY,
      queryParams,
      (response) => {
        callBack(response);
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem('token')
    );
  };
};

export const postTheReward = (
  payload,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    postAPI(
      endpoints.GRANT_REWARD,

      payload,
      (response) => {
        callBack(response);
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem('token')
    );
  };
};
export const getAllComments = (
  ticketId,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    getAPI(
      `${endpoints.COMMENTS}${ticketId}`,
      {},
      (response) => {
        callBack(response);
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem('token')
    );
  };
};

export const createComments = (
  payload,
  ticketId,
  callBack,
  errorCallback
) => {
  return (dispatch) => {
    postAPI(
      `${endpoints.COMMENTS}${ticketId}`,
      payload,
      (response) => {
        callBack(response);
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem('token')
    );
  };
};
